import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { api } from '@/lib/api/client';
import { formatBillingPeriodDate } from '@/lib/utils/billingPeriod';

const DEFAULT_BILLING_PARAMS: Record<string, unknown> = {};

interface BillingPeriodItem {
  id: number;
  startDate: string;
  endDate: string;
  billId?: number;
  [key: string]: unknown;
}

function billingPeriodLabel(item: BillingPeriodItem): string {
  return `${formatBillingPeriodDate(item?.startDate, true)} - ${formatBillingPeriodDate(item?.endDate)}`;
}

/**
 * A period is "current" if today falls within [startDate, endDate].
 * An open-ended period (no endDate) counts as current once it has started.
 * Matches the rule the backend uses in ContractsService.findAll's
 * `billingPeriods` filter.
 */
function isCurrentBillingPeriod(item: BillingPeriodItem): boolean {
  if (!item?.startDate) return false;
  const now = new Date();
  const start = new Date(item.startDate);
  if (Number.isNaN(start.getTime()) || start > now) return false;
  if (!item.endDate) return true;
  const end = new Date(item.endDate);
  if (Number.isNaN(end.getTime())) return false;
  return end >= now;
}

interface BillingPeriodItemLabelProps {
  item: BillingPeriodItem;
}

function BillingPeriodItemLabel({ item }: BillingPeriodItemLabelProps) {
  return <>{billingPeriodLabel(item)}</>;
}

interface UniversalComboboxBillingPeriodProps {
  url: string;
  labelKey?: string;
  valueKey?: string;
  onSelectItem?: (item: BillingPeriodItem | null) => void;
  placeholder?: string;
  queryKey?: string;
  params?: Record<string, unknown>;
  className?: string;
  selectedId?: number | null;
  disabled?: boolean;
}

export function UniversalComboboxBillingPeriod({
  url,
  labelKey = 'name',
  valueKey = 'id',
  onSelectItem,
  placeholder = 'Select...',
  queryKey = 'comboData',
  params = DEFAULT_BILLING_PARAMS,
  className = '',
  selectedId = null,
  disabled = false,
}: UniversalComboboxBillingPeriodProps) {
  const { t } = useTranslation();
  const listboxId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedItem, setSelectedItem] = React.useState<BillingPeriodItem | null>(null);
  const selectedItemRef = React.useRef<BillingPeriodItem | null>(null);
  selectedItemRef.current = selectedItem;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const setInputEl = React.useCallback((el: HTMLInputElement | null) => {
    (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  }, []);

  React.useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  const [debounced, setDebounced] = React.useState('');
  const debouncedSet = React.useMemo(
    () =>
      debounce((val: string) => {
        setDebounced(val);
      }, 300),
    []
  );
  React.useEffect(() => {
    debouncedSet(search);
    return () => debouncedSet.cancel();
  }, [search, debouncedSet]);

  const [filters, setFilters] = React.useState({
    limit: 10,
    page: 0,
    ...params,
  });
  const paramsString = React.useMemo(() => JSON.stringify(params ?? {}), [params]);

  React.useEffect(() => {
    const parsed = JSON.parse(paramsString);
    setFilters((prev) => ({
      ...prev,
      ...parsed,
      page: 0,
      s: debounced && debounced.length >= 2 ? debounced : undefined,
    }));
  }, [paramsString, debounced]);

  const { data, refetch, isFetching } = useQuery({
    queryKey: [queryKey, filters],
    queryFn: async () => {
      const offset = (filters.page ?? 0) * (filters.limit ?? 10);
      const q: Record<string, unknown> = { ...filters, offset };
      delete q.page;

      for (const k of Object.keys(q)) {
        const v = q[k];
        if (v === null || v === undefined || v === '') delete q[k];
      }

      return api.get(url, { params: q });
    },
    enabled: open && !disabled,
    retry: false,
  });

  const items: BillingPeriodItem[] = data?.results ?? [];

  React.useEffect(() => {
    if (!selectedId) {
      setSelectedItem(null);
      return;
    }
    // Already have the correct item — nothing to do (prevents clearing after popover close)
    if (selectedItemRef.current?.[valueKey] === selectedId) return;

    const found = items.find((it) => it?.[valueKey] === selectedId);
    if (found) {
      setSelectedItem(found);
      return;
    }
    // Hydrate a pre-existing selectedId (e.g. page load) — don't call onSelectItem
    // since the parent already knows the id, we just need the label data
    (async () => {
      try {
        const item = await api.get<BillingPeriodItem>(`${url}/${selectedId}`);
        setSelectedItem(item);
      } catch {
        // Keep existing selection on error
      }
    })();
  }, [selectedId, url, valueKey, items]);

  const handleSelect = (item: BillingPeriodItem) => {
    const already = selectedItem?.[valueKey] === item?.[valueKey];
    if (already) {
      setSelectedItem(null);
      onSelectItem?.(null);
    } else {
      setSelectedItem(item);
      onSelectItem?.(item);
    }
    setOpen(false);
  };

  return (
    <Popover
      open={!disabled && open}
      onOpenChange={(state) => {
        if (disabled) return;
        setOpen(state);
        if (state) {
          refetch();
          setSearch('');
        } else {
          setSearch('');
          setFilters((prev) => ({ ...prev, s: undefined, page: 0 }));
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          className={cn('justify-between overflow-hidden', className)}
          disabled={disabled}
        >
          {selectedItem ? (
            <span className="flex items-center gap-2 truncate">
              <span className="truncate">{billingPeriodLabel(selectedItem)}</span>
              {isCurrentBillingPeriod(selectedItem) && (
                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                  {t('current') || 'Current'}
                </span>
              )}
            </span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      {!disabled && (
        <PopoverContent className="w-[300px] p-0" forceMount>
          <Command>
            <CommandInput
              ref={setInputEl}
              value={search}
              onValueChange={(v) => setSearch(v ?? '')}
              placeholder={t('search') || 'Search...'}
              className="h-9"
            />
            <CommandList id={listboxId}>
              <CommandEmpty>
                {isFetching
                  ? t('loading') || 'Loading...'
                  : search.length > 0 && search.length < 2
                    ? t('keepTyping') || 'Keep typing (>= 2 characters)...'
                    : t('noDataFound') || 'No data found.'}
              </CommandEmpty>

              <CommandGroup>
                {items.map((item) => {
                  const key = String(item?.[valueKey] ?? '');
                  const valueStr = String(item?.[labelKey] ?? key);
                  const disabledItem = !!item?.billId;
                  const current = isCurrentBillingPeriod(item);

                  return (
                    <CommandItem
                      key={key}
                      value={valueStr}
                      onSelect={() => !disabledItem && handleSelect(item)}
                      disabled={disabledItem}
                      className={cn(
                        disabledItem && 'opacity-50 cursor-not-allowed',
                        current && 'bg-primary/5 font-semibold text-primary'
                      )}
                    >
                      <BillingPeriodItemLabel item={item} />
                      {current && (
                        <span className="ml-2 shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          {t('current') || 'Current'}
                        </span>
                      )}
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          selectedItem?.[valueKey] === item?.[valueKey]
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
}
